import { buildDocumentsQueryKey } from '@dealstack/frontend/shared/components/Documents/hooks/utils/buildDocumentsQueryKey';
import { TableOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { DocumentLibraryService } from '@dealstack/frontend/shared/dataExchange/services/document-library/document-library.service';
import {
	DocumentFieldFilter,
	DocumentLinkTarget,
} from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface Props {
	dealId: string;
	documentId: string;
	target?: DocumentLinkTarget;
	dataOperations?: TableOperations<DocumentFieldFilter>;
}

export const useAddPersonsToADocument = ({ dealId, documentId, target, dataOperations }: Props) => {
	const queryClient = useQueryClient();
	const { isPending, mutateAsync } = useMutation({
		mutationKey: [
			formatUnicorn(
				API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_ADD_PERSONS_TO_DOCUMENT_DOCUMENTID_POST,
				{
					dealId,
					documentId,
				}
			),
		],

		mutationFn: DocumentLibraryService.addPersonsToDocument,
	});

	const handleAddPersons = async (documentId: string, personIds: string[]): Promise<boolean> => {
		try {
			await mutateAsync({ dealId, documentId, personIds });
			await queryClient.refetchQueries({
				queryKey: [
					formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_DOCUMENT_DOCUMENTID_GET, {
						dealId,
						documentId,
					}),
				],
			});

			// Reload the Document Library table. Prefix key match refetches every
			// filter variant; a no-op in contexts where that query isn't mounted.
			await queryClient.refetchQueries({
				queryKey: [
					formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_DOCUMENTS_GET, {
						dealId,
					}),
				],
			});

			// refresh documents list
			if (target) {
				await queryClient.refetchQueries({
					queryKey: [
						formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_GET, { dealId }),
						buildDocumentsQueryKey({
							target,
							dataOperations,
						}),
					],
				});

				await queryClient.refetchQueries({
					queryKey: [
						formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_DOCUMENTID_GET, {
							dealId,
							documentId,
						}),
						{ transactionId: target.transactionId },
					],
				});
			}

			showToastV2({
				message: 'Persons added successfully',
				title: 'Success',
				state: 'success',
			});

			return true;
		} catch {
			showToastV2({
				message: 'Error adding persons',
				title: 'Error',
				state: 'error',
			});

			return false;
		}
	};

	return { handleAddPersons, isLoading: isPending };
};
